// components/SummaryView.js
// Shared by both clinician-facing surfaces (normal-flow Clinician and
// staff TelehealthReviewDashboard, via their own dedicated SummaryViewPage)
// — search a linked patient, generate a visual, all-report-types-at-once
// summary (reusing ScoreDial so the visual language matches every other
// report in the app), and download it as a branded PDF.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Autocomplete, TextField, Button, CircularProgress, Alert, Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import axios from "axios";
import ScoreDial from "./reports/ScoreDial";
import { levelColor, levelBg, gradeColor } from "./reports/reportHelpers";
import "./print-summary-view.css";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyMid: "#1a2244", accent: "#637cdb" };

const REPORT_TYPE_LABELS = {
  opioid_risk: "Opioid Risk",
  pain_perception: "Pain Perception",
  pgx: "Drug Metabolism",
};

const STAT_LABELS = {
  proove_ori: "Proove Opioid Risk",
  ort: "Opioid Risk Tool (ORT)",
  phq2: "Personal Health Questionnaire (PHQ-2)",
  self_reported_pain: "Self-Reported Pain",
  pss: "Perceived Stress Scale (PSS)",
  comt_predisposition: "Genetic Predisposition (COMT)",
};

function StatDial({ label, stat, isDark, index }) {
  if (!stat || (stat.level == null && stat.score == null)) return null;
  const { level, score, range } = stat;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06, ease: "easeOut" }}
      style={{ flex: "1 1 180px", minWidth: 170 }}
    >
      <Box sx={{
        borderRadius: "16px", p: 2.5, textAlign: "center", height: "100%",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}`,
        boxShadow: isDark ? "none" : "0 2px 10px rgba(10,14,39,0.04)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 10px 28px rgba(10,14,39,0.1)" },
      }}>
        <Typography sx={{
          fontSize: 11.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b",
          mb: 1.5, textTransform: "uppercase", letterSpacing: "0.04em",
        }}>
          {label}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ScoreDial level={level} score={score} />
        </Box>
        <Box sx={{
          mt: 1.5, display: "inline-block", borderRadius: 999, px: 1.75, py: 0.5,
          fontSize: 12, fontWeight: 700, color: levelColor(level), bgcolor: levelBg(level),
        }}>
          {level || "N/A"}
        </Box>
        {range && <Typography sx={{ mt: 0.75, fontSize: 10.5, color: isDark ? "#64748b" : "#94a3b8" }}>Range: {range}</Typography>}
      </Box>
    </motion.div>
  );
}

export default function SummaryView({ variant, token }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const myPatientsUrl = variant === "telehealth"
    ? `${API_BASE}/telehealth-review/my-patients`
    : `${API_BASE}/clinician/my-patients`;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tables, setTables] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoadingPatients(true);
    axios.get(myPatientsUrl, { headers })
      .then((res) => { if (mounted) setPatients(res.data.patients || []); })
      .catch(() => { if (mounted) setError("Couldn't load your patient list."); })
      .finally(() => { if (mounted) setLoadingPatients(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, token]);

  const generate = async (patient) => {
    if (!patient) return;
    setLoading(true);
    setError(null);
    setSummary(null);
    setTables({});
    try {
      const res = await axios.get(`${API_BASE}/clinician/patient-summary`, {
        headers,
        params: { name_hash: patient.name_hash || "", dob_hash: patient.dob_hash || "" },
      });
      setSummary(res.data.summary || {});
      setTables(res.data.tables || {});
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't generate the summary view.");
    } finally {
      setLoading(false);
    }
  };

  // No server round-trip, no separate PDF template that can drift out of
  // sync with what's on screen — this just prints the same dials/summary
  // the clinician is already looking at. print-summary-view.css (imported
  // below) hides everything except the #summary-view-printable region and
  // strips the app chrome/backgrounds when the browser's print dialog opens.
  //
  // 2026-09-21: the print-only CSS is now gated behind this class (added
  // right before printing, removed right after) instead of being an
  // unscoped `body *` rule — that used to leak into react-to-print's copied
  // stylesheets on completely unrelated pages (Opioid Risk / Pain
  // Perception / Drug Metabolism reports) and blank out their printed
  // output. "afterprint" fires whether the user actually prints or cancels
  // the dialog, so the class never gets stuck on.
  const downloadPdf = () => {
    document.body.classList.add("printing-summary-view");
    const cleanup = () => document.body.classList.remove("printing-summary-view");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
  };

  const textSub = isDark ? "#94a3b8" : "#64748b";
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)";
  // Union of both — a report type can show up in "tables" only (Drug
  // Metabolism, no numeric score) or "stats" only (Opioid Risk/Pain
  // Perception today), so neither field alone tells us what to render.
  const reportTypesShown = summary
    ? Array.from(new Set([...Object.keys(summary), ...Object.keys(tables)]))
    : [];

  return (
    <Box>
      {/* Patient picker */}
      <Box sx={{
        display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap",
        borderRadius: "18px", p: { xs: 2.5, sm: 3 },
        background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyMid} 100%)`,
      }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: "12px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: "rgba(99,124,219,0.18)", border: "1px solid rgba(99,124,219,0.3)",
        }}>
          <PersonSearchRoundedIcon sx={{ color: "#a8b6f0", fontSize: 22 }} />
        </Box>

        <Autocomplete
          sx={{ flex: "1 1 280px", minWidth: 220 }}
          options={patients}
          loading={loadingPatients}
          value={selected}
          onChange={(_, value) => { setSelected(value); setSummary(null); setError(null); }}
          getOptionLabel={(p) => `${p.first_name} ${p.last_name}`}
          isOptionEqualToValue={(a, b) => a.name_hash === b.name_hash && a.dob_hash === b.dob_hash}
          noOptionsText="No patients linked to your account yet"
          renderOption={(props, p) => (
            <Box component="li" {...props} key={`${p.name_hash}:${p.dob_hash}`}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.first_name} {p.last_name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{p.date_of_birth || "DOB unknown"}</Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search for a patient…"
              variant="filled"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <>
                    {loadingPatients && <CircularProgress size={16} sx={{ color: "#a8b6f0", mr: 1 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
                sx: {
                  bgcolor: "rgba(255,255,255,0.08)", borderRadius: "12px",
                  color: "#fff", fontSize: 14,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                  "&.Mui-focused": { bgcolor: "rgba(255,255,255,0.12)" },
                },
              }}
              sx={{ "& .MuiFilledInput-input": { py: "12px !important" } }}
            />
          )}
        />

        <Button
          variant="contained"
          disabled={!selected || loading}
          onClick={() => generate(selected)}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: "#0a0e27" }} /> : <AutoAwesomeRoundedIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: "#fff", color: BRAND.navy, fontWeight: 700, fontSize: 13.5,
            textTransform: "none", borderRadius: "12px", px: 2.5, py: 1.2,
            flexShrink: 0,
            "&:hover": { bgcolor: "#eef1f8" },
            "&.Mui-disabled": { bgcolor: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)" },
          }}
        >
          {loading ? "Generating…" : "Generate Summary View"}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}

      <AnimatePresence mode="wait">
        {summary && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            {reportTypesShown.length === 0 ? (
              <Box sx={{
                mt: 3, p: 4, textAlign: "center", borderRadius: "16px",
                border: `1px dashed ${border}`,
              }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: textSub, mb: 1 }} />
                <Typography sx={{ fontSize: 14, color: textSub, fontWeight: 600 }}>
                  No reports found for this patient yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 3 }} id="summary-view-printable">
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  mb: 2, flexWrap: "wrap", gap: 1.5,
                }} className="no-print">
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: textSub }}>
                    {selected?.first_name} {selected?.last_name} &middot; {reportTypesShown.length} report{reportTypesShown.length !== 1 ? "s" : ""}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={downloadPdf}
                    startIcon={<PictureAsPdfRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none", fontWeight: 700, fontSize: 12.5,
                      borderRadius: "10px", borderColor: border,
                      color: isDark ? "#e2e8f0" : BRAND.navy,
                    }}
                  >
                    Print / Save as PDF
                  </Button>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {reportTypesShown.map((reportType) => (
                    <Box key={reportType}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                        <Chip
                          size="small"
                          label={REPORT_TYPE_LABELS[reportType] || reportType}
                          sx={{
                            bgcolor: isDark ? "rgba(99,124,219,0.15)" : "rgba(99,124,219,0.1)",
                            color: BRAND.accent, fontWeight: 700, fontSize: 11.5,
                            letterSpacing: "0.03em",
                          }}
                        />
                      </Box>
                      {/* Drug Metabolism has no single numeric score to put on
                          a dial — shows its extracted enzyme table instead,
                          same pattern already used on the patient-side
                          homepage panel (HomeSummaryPanel.js). */}
                      {reportType === "pgx" && (tables[reportType] || []).length > 0 ? (
                        <Box sx={{ overflowX: "auto" }}>
                          <Box component="table" sx={{
                            width: "100%", borderCollapse: "collapse", fontSize: 12.5,
                            "& th": {
                              textAlign: "left", padding: "5px 10px", fontWeight: 700,
                              color: isDark ? "#94a3b8" : "#64748b", fontSize: 11,
                              textTransform: "uppercase", letterSpacing: "0.03em",
                            },
                            "& td": {
                              padding: "5px 10px", borderTop: `1px solid ${border}`,
                              color: isDark ? "#e2e8f0" : "#1e293b", verticalAlign: "top",
                            },
                          }}>
                            <thead>
                              <tr>
                                {(tables[reportType][0]?.enzyme !== undefined
                                  ? ["Enzyme", "Grade", "Medications"]
                                  : ["Gene", "Genotype", "Phenotype"]
                                ).map((h) => <th key={h}>{h}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {tables[reportType].map((row, i) => (
                                <tr key={i}>
                                  {row.enzyme !== undefined ? (
                                    <>
                                      <td style={{ fontWeight: 600 }}>{row.enzyme}</td>
                                      <td>
                                        {row.grade ? (
                                          <Box sx={{
                                            display: "inline-flex", alignItems: "center", gap: 0.75,
                                            borderRadius: 999, px: 1, py: 0.25,
                                            bgcolor: gradeColor(row.grade).bg, color: gradeColor(row.grade).fg,
                                            fontWeight: 700, fontSize: 11.5, whiteSpace: "nowrap",
                                          }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: gradeColor(row.grade).fg, flexShrink: 0 }} />
                                            {row.grade}
                                          </Box>
                                        ) : "—"}
                                      </td>
                                      <td>{row.medications || "—"}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td style={{ fontWeight: 600 }}>{row.gene}</td>
                                      <td>{row.genotype || "—"}</td>
                                      <td>{row.phenotype || "—"}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                          {Object.entries(summary[reportType] || {}).map(([key, stat], i) => (
                            <StatDial key={key} label={STAT_LABELS[key] || key} stat={stat} isDark={isDark} index={i} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* One shared disclaimer for the whole page — matches the old
                    Proove "Summary View" page's own convention (one FDA-clearance
                    note at the bottom, not repeated per report-type section). */}
                <Typography sx={{ mt: 2.5, fontSize: 10, lineHeight: 1.6, color: textSub, opacity: 0.85 }}>
                  These test(s) have not been cleared or approved by the FDA; such clearance is not required.
                  Results are not intended as the sole means for clinical diagnosis or patient management —
                  please discuss with a licensed healthcare provider.
                </Typography>
              </Box>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
